import { useState, useEffect } from "react";
import { useAuth } from "@/lib/auth-context";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Loader2,
  Shield,
  Building2,
  Zap,
  Globe2,
  ArrowRight,
  Sparkles,
  Mail,
  KeyRound
} from "lucide-react";
import { cn } from "@/lib/utils";

export default function Login() {
  const { login, user, isLoading: authLoading } = useAuth();
  const [, setLocation] = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Redirect if already logged in
  useEffect(() => {
    if (!authLoading && user) {
      setLocation("/");
    }
  }, [user, authLoading, setLocation]);

  // Show loading while checking auth state
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-hero">
        <div className="relative">
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-glow-primary animate-pulse">
            <img src="/vedalogic-logo.svg" alt="VedaLogic" className="h-8 w-8 object-contain" />
          </div>
          <div className="absolute inset-0 rounded-xl animate-pulse-ring" />
        </div>
      </div>
    );
  }

  // Don't show login form if already logged in (prevents flash)
  if (user) {
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      await login(email, password);
      // Login successful - redirect will happen via useEffect
    } catch (err: any) {
      console.error("Login error:", err);
      setError(err.message || "Login failed. Please check your credentials.");
      setIsLoading(false);
    }
  };

  const features = [
    {
      icon: Building2,
      title: "Multi-Tenant Platform",
      description: "Manage programs, corporates, and cardholders seamlessly"
    },
    {
      icon: Shield,
      title: "Bank-Grade Security",
      description: "PCI-DSS compliant with real-time fraud controls"
    },
    {
      icon: Globe2,
      title: "Global Networks",
      description: "Visa, Mastercard, and RuPay integration"
    },
    {
      icon: Zap,
      title: "Instant Issuance",
      description: "Issue virtual cards in milliseconds"
    }
  ];

  return (
    <div className="min-h-screen flex">
      {/* Left side - Premium Hero Section */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden">
        {/* Animated gradient background */}
        <div className="absolute inset-0 bg-gradient-hero" />

        {/* Mesh gradient overlay */}
        <div className="absolute inset-0 bg-gradient-mesh opacity-60" />

        {/* Floating geometric shapes */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Large teal orb */}
          <div
            className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-primary/20 to-primary/5 blur-3xl animate-float"
            style={{ animationDelay: "0s", animationDuration: "8s" }}
          />
          {/* Gold accent orb */}
          <div
            className="absolute top-1/3 -left-20 w-[300px] h-[300px] rounded-full bg-gradient-to-br from-amber-500/15 to-orange-500/5 blur-3xl animate-float"
            style={{ animationDelay: "2s", animationDuration: "10s" }}
          />
          {/* Bottom teal orb */}
          <div
            className="absolute -bottom-40 right-1/4 w-[400px] h-[400px] rounded-full bg-gradient-to-tr from-primary/15 to-transparent blur-3xl animate-float"
            style={{ animationDelay: "4s", animationDuration: "12s" }}
          />

          {/* Geometric shapes */}
          <div
            className="absolute top-20 right-20 w-20 h-20 border border-white/10 rounded-2xl rotate-12 animate-float"
            style={{ animationDelay: "1s", animationDuration: "6s" }}
          />
          <div
            className="absolute bottom-32 left-32 w-16 h-16 border border-primary/20 rounded-xl -rotate-12 animate-float"
            style={{ animationDelay: "3s", animationDuration: "7s" }}
          />
          <div
            className="absolute top-1/2 right-40 w-12 h-12 border border-amber-500/20 rounded-lg rotate-45 animate-float"
            style={{ animationDelay: "5s", animationDuration: "9s" }}
          />
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          {/* Logo */}
          <div className="animate-reveal-up" style={{ animationDelay: "0.1s" }}>
            <img src="/vedalogic-logo.svg" alt="VedaLogic" className="h-16 w-auto object-contain" />
          </div>

          {/* Hero content */}
          <div className="space-y-10 max-w-lg">
            <div className="space-y-6 animate-reveal-up" style={{ animationDelay: "0.2s" }}>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/10 backdrop-blur-sm">
                <Sparkles className="h-3.5 w-3.5 text-amber-400" />
                <span className="text-xs font-medium text-white/80">Enterprise-Grade Platform</span>
              </div>

              <h1 className="text-5xl font-display font-bold leading-[1.1] tracking-tight">
                Corporate Virtual Card
                <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-primary via-teal-300 to-primary">
                  Management Platform
                </span>
              </h1>

              <p className="text-lg text-white/60 leading-relaxed">
                Issue, manage, and control virtual cards across your organization with enterprise-grade security and real-time analytics.
              </p>
            </div>

            {/* Feature cards grid */}
            <div className="grid grid-cols-2 gap-4">
              {features.map((feature, index) => (
                <div
                  key={feature.title}
                  className={cn(
                    "group p-4 rounded-2xl backdrop-blur-md border transition-all duration-300",
                    "bg-white/[0.03] border-white/[0.08] hover:bg-white/[0.06] hover:border-white/[0.15]",
                    "animate-reveal-up"
                  )}
                  style={{ animationDelay: `${0.3 + index * 0.1}s` }}
                >
                  <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/20 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300">
                    <feature.icon className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-semibold text-sm text-white mb-1">{feature.title}</h3>
                  <p className="text-xs text-white/50 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between animate-reveal-up" style={{ animationDelay: "0.7s" }}>
            <p className="text-xs text-white/30">
              &copy; 2026 VedaLogic. All rights reserved.
            </p>
            <div className="flex items-center gap-4">
              <a href="/privacy-policy" className="text-xs text-white/40 hover:text-white/60 transition-colors">Privacy</a>
              <a href="#" className="text-xs text-white/40 hover:text-white/60 transition-colors">Terms</a>
              <a href="https://vedalogic.betteruptime.com/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs text-white/40 hover:text-white/60 transition-colors">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                Status
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-background relative">
        {/* Subtle gradient overlay for right side */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.02] via-transparent to-amber-500/[0.02]" />

        <div className="w-full max-w-sm relative z-10">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8 animate-reveal-up">
            <img src="/vedalogic-logo.svg" alt="VedaLogic" className="h-12 w-auto mx-auto mb-2 object-contain brightness-0 dark:brightness-100" />
            <p className="text-sm text-muted-foreground">Card Management Platform</p>
          </div>

          {/* Login card */}
          <div
            className="p-8 rounded-3xl bg-card border-2 border-card-border shadow-card animate-reveal-up"
            style={{ animationDelay: "0.1s" }}
          >
            <div className="mb-6">
              <h2 className="text-2xl font-display font-bold text-foreground mb-2">Welcome back</h2>
              <p className="text-muted-foreground">Sign in to your admin account</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <Alert variant="destructive" className="animate-reveal-up">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-foreground">
                  Email Address
                </Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={isLoading}
                    className="pl-11"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-sm font-medium text-foreground">
                    Password
                  </Label>
                  <a href="#" className="text-xs text-primary hover:text-primary/80 transition-colors font-medium">
                    Forgot password?
                  </a>
                </div>
                <div className="relative">
                  <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isLoading}
                    className="pl-11"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 font-semibold text-base group"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  <>
                    Sign In
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </>
                )}
              </Button>
            </form>
          </div>

          <p className="mt-6 text-center text-sm text-muted-foreground animate-reveal-up" style={{ animationDelay: "0.2s" }}>
            Need access? <a href="#" className="text-primary hover:text-primary/80 font-medium transition-colors">Contact your administrator</a>
          </p>

          {/* Trust badges */}
          <div className="mt-8 flex items-center justify-center gap-6 animate-reveal-up" style={{ animationDelay: "0.3s" }}>
            <div className="flex items-center gap-2 text-muted-foreground/60">
              <Shield className="h-4 w-4" />
              <span className="text-xs">PCI-DSS</span>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground/60">
              <Globe2 className="h-4 w-4" />
              <span className="text-xs">SOC 2</span>
            </div>
            <a href="https://vedalogic.betteruptime.com/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-muted-foreground/60 hover:text-muted-foreground transition-colors">
              <Zap className="h-4 w-4" />
              <span className="text-xs">99.9% Uptime</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
